/**
 * Email thread document schema and paths.
 *
 * A thread groups the inbound emails of one conversation, keyed on the email's
 * {@link https://developers.google.com/gmail/imap/imap-extensions | X-GM-THRID}
 * when available, or a derived RFC-822 root otherwise (see `deriveThreadId` in
 * the functions `pollInboxLib`). It lives under the same bucket as its emails
 * (`reports/{bucketId}/threads/{threadId}`) and denormalizes conversation-level
 * metadata so the triage UI can render a grouped thread row without reading
 * every email in the thread.
 *
 * Server-written only (`processEmail` upserts it as emails are ingested). The
 * base schema uses {@link FirestoreTimestampSchema} so the admin/ and client/
 * SDK modules can extend it with their own timestamp transforms.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';
import {getBucketDocPath} from './reportBucket';

/**
 * Base schema for a thread document at `reports/{bucketId}/threads/{threadId}`.
 *
 * `subject` / `lastMessageFrom` track the most recently ingested message so the
 * list row shows the newest state; `messageCount` is denormalized so the row
 * can show a count without reading the `emails` subcollection.
 */
export const ThreadDocumentSchema = z
  .object({
    /** Subject of the most recently ingested message in the thread. */
    subject: z.string(),
    /**
     * Classifier category of the most recently ingested message, or `null`
     * when unclassified. Denormalized so the thread row can show it (mirrors
     * the per-email category the flat inbox used to display).
     */
    category: z.string().nullable(),
    /** Sender (`"Name <addr>"`) of the most recently ingested message. */
    lastMessageFrom: z.string(),
    /** Server time the most recent message in the thread was received. */
    lastMessageAt: FirestoreTimestampSchema,
    /** Server time the first message in the thread was received. */
    firstMessageAt: FirestoreTimestampSchema,
    /** Number of ingested messages in this thread (denormalized). */
    messageCount: z.number(),
    /**
     * Course bucket this thread lives under, or `null` for the bulk
     * (`_uncategorized`) bucket. Denormalized from the bucket path.
     */
    courseId: z.string().nullable(),
    /** Server time the thread doc was last updated. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ThreadDocumentSchema}. */
export type ThreadDocument = z.infer<typeof ThreadDocumentSchema>;

/** Subcollection (under a bucket) holding that bucket's email threads. */
export const THREADS_SUBCOLLECTION = 'threads' as const;

/**
 * Threads subcollection path for a bucket.
 *
 * @param args.bucketId - A course id, or the bulk-bucket id.
 * @returns Firestore collection path `reports/{bucketId}/threads`.
 */
export function getThreadsCollectionPath({
  bucketId,
}: {
  bucketId: string;
}): string {
  return `${getBucketDocPath({bucketId})}/${THREADS_SUBCOLLECTION}`;
}

/**
 * Single thread document path.
 *
 * @param args.bucketId - The bucket the thread lives in.
 * @param args.threadId - The (sanitized) conversation id.
 * @returns Firestore doc path `reports/{bucketId}/threads/{threadId}`.
 */
export function getThreadDocumentPath({
  bucketId,
  threadId,
}: {
  bucketId: string;
  threadId: string;
}): string {
  return `${getThreadsCollectionPath({bucketId})}/${threadId}`;
}
